import React from 'react'
import * as RadixTooltip from '@radix-ui/react-tooltip'
import { Loader } from './loader'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { createPopper } from '@popperjs/core'
import { isMobileSize } from '../../utils'
import { clear } from 'console'

interface TooltipProps {
  /** The children to wrap the tooltip around. This must be a single element that forwards refs and spreads props. */
  children: React.ReactNode
  /** The text to display in the tooltip. */
  text?: React.ReactNode
  /** Callback function to handle tooltip open/close events. */
  onOpenChange?: (open: boolean) => void
  /** Whether the tooltip is open or closed. If uncontrolled, this tooltip shows on hover. */
  open?: boolean
  /** Offset for the tooltip position from the trigger element. */
  offset?: { x: number; y: number }
  /** Alignment of the tooltip relative to the trigger element. */
  align?: 'start' | 'center' | 'end'
  /** Side of the trigger element where the tooltip should appear. */
  side?: 'top' | 'right' | 'bottom' | 'left'
  /** Whether to allow hovering content inside the tooltip. */
  allowHoveringContent?: boolean
  /** Whether to show the tooltip on mobile devices. */
  tooltipOnMobile?: boolean
}

const anim =
  'data-[state=delayed-open]:animate-fadeIn data-[state=instant-open]:animate-fadeIn data-[state=closed]:animate-fadeOut'
const tooltipClasses = `z-[9999] max-w-[300px] rounded bg-neutral-900 p-1 px-[7px] py-[3px] text-white break-words whitespace-normal outline outline-1 outline-neutral-900 ${anim}`

/**
 * Tooltip component that wraps the RadixTooltip and expects the child to:
 * - Forward refs using `React.forwardRef`.
 * - Spread props onto the child component.
 *
 * This is necessary for Radix UI's `Tooltip.Trigger` (which sets props on `children`) to work properly.
 */
export const Tooltip: React.FC<TooltipProps> = ({
  children,
  text,
  onOpenChange,
  open,
  offset = { x: 0, y: 5 },
  align = 'center',
  side = 'top',
  allowHoveringContent = false,
  tooltipOnMobile = false,
}) => {
  const [openOverride, setOpenOverride] = React.useState<boolean | undefined>(undefined)
  if (!text) return children
  return (
    <RadixTooltip.Root
      onOpenChange={(o) => {
        if (openOverride) setOpenOverride(undefined)
        onOpenChange?.(o)
      }}
      open={openOverride === undefined ? open : openOverride}
      disableHoverableContent={!allowHoveringContent}
    >
      <RadixTooltip.Trigger
        asChild
        onTouchStart={
          tooltipOnMobile
            ? (e) => {
                if (!isMobileSize()) return
                e.stopPropagation()
                e.preventDefault()
                setTimeout(() => setOpenOverride(true), 0)
              }
            : undefined
        }
      >
        {children}
      </RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          collisionPadding={5}
          sideOffset={offset.y}
          alignOffset={offset.x}
          side={side}
          align={align}
          className={tooltipClasses}
        >
          {text}
          <RadixTooltip.Arrow className="text-neutral-900" />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  )
}

interface RemoteTooltipProps<T> {
  /** The children to wrap the tooltip around. This must be a single element that forwards refs and spreads props. */
  children: React.ReactNode
  /** The URL and data to fetch the content from. */
  loadFrom: { url: string; data: Record<string, any> }
  /** Function to process the result of the fetch into a React node. */
  processResult: (result: T) => React.ReactNode
}

/**
 * RemoteTooltip component that fetches data, processes it and passes it to the Tooltip component.
 * The child of this component must also:
 * - Forward refs using `React.forwardRef`.
 * - Spread props onto the child component.
 *
 * This ensures that the Radix Tooltip functionality works correctly with the passed `children`.
 */
export const RemoteTooltip = <T,>({ children, loadFrom, processResult }: RemoteTooltipProps<T>) => {
  const [response, setResponse] = React.useState<React.ReactNode | undefined>(undefined)
  const everOpenedRef = React.useRef(false)

  const load = () => {
    everOpenedRef.current = true
    const startLoadingTime = performance.now()
    makeRequest<T>(loadFrom.url, loadFrom.data)
      .then((r) =>
        // this behaves weirdly if the tooltip opens and immediately changes content, so we require 200ms minimum delay
        setTimeout(() => setResponse(processResult(r)), Math.max(0, 200 - (performance.now() - startLoadingTime)))
      )
      .catch(showAxiosErrorAsToast)
  }

  return (
    <Tooltip
      onOpenChange={(open) => {
        if (open && !everOpenedRef.current) load()
      }}
      text={response || <Loader color="text-white" />}
    >
      {children}
    </Tooltip>
  )
}

interface RemoteHTMLTooltipProps {
  /** The children to wrap the tooltip around. This must be a single element that forwards refs and spreads props. */
  children: React.ReactNode
  /** The URL and data to fetch the HTML content from. */
  loadFrom: { url: string; data: Record<string, any> }
}

/*
 * RemoteHTMLTooltip component that fetches html and passes it to the Tooltip component. We should move away from using this
 * The child of this component must also:
 * - Forward refs using `React.forwardRef`.
 * - Spread props onto the child component.
 *
 * This ensures that the Radix Tooltip functionality works correctly with the passed `children`.
 */
export const RemoteHTMLTooltip = ({ children, loadFrom }: RemoteHTMLTooltipProps) => (
  <RemoteTooltip<string> loadFrom={loadFrom} processResult={(r) => <div dangerouslySetInnerHTML={{ __html: r }} />}>
    {children}
  </RemoteTooltip>
)

export type ImperitiveTooltip = {
  showTooltip: (ms: number) => void
  hideTooltip: () => void
  removeTooltip: () => void
}

export type ImperitiveTooltipOptions = {
  allowHoveringContent?: boolean
  showForMs?: number
  overwriteExisting?: boolean
}

type ImperitiveTooltipElement = Element & { imperitiveTooltip: ImperitiveTooltip }

/**
 * Shows a tooltip imperatively on the specified element.
 * Warning: If the target re-mounts, the tooltip will be removed. This tooltip
 * exists outside of the React tree and is not managed by React, and should be
 * used for showing simple non-crucial message on far parts of the react tree.
 *
 *
 * @param selector - The selector or element to attach the tooltip to.
 * @param tooltipText - The text to display in the tooltip.
 * @param options - Options for the tooltip, including:
 *  - `allowHoveringContent`: Whether to allow hovering content inside the tooltip.
 * - `showForMs`: Duration to show the tooltip in milliseconds.
 * - `overwriteExisting`: Whether to overwrite an existing tooltip on the element.
 * @returns An object with methods to show, hide, and remove the tooltip.
 */
export const showImperitiveTooltip = (
  selector: string | Element,
  tooltipText: string,
  options: ImperitiveTooltipOptions = {}
) => {
  const targetElement = (
    selector instanceof Element ? selector : document.querySelector(selector)
  ) as ImperitiveTooltipElement
  if (!targetElement) throw new Error(`Element with selector "${selector}" not found.`)
  if (!options.overwriteExisting && targetElement.imperitiveTooltip) return targetElement.imperitiveTooltip
  if (targetElement.imperitiveTooltip) targetElement.imperitiveTooltip.removeTooltip()

  const tooltipElement = document.createElement('div')
  tooltipElement.textContent = tooltipText
  tooltipElement.className = tooltipClasses
  document.body.appendChild(tooltipElement)

  const arrowElement = document.createElement('div')
  arrowElement.className = 'bg-neutral-900'
  arrowElement.style.cssText = `
    width: 8px;
    height: 8px;
    position: absolute;
    clip-path: polygon(50% 0%, 0% 50%, 50% 100%, 100% 50%);
  `
  tooltipElement.appendChild(arrowElement)

  const popperInstance = createPopper(targetElement, tooltipElement, {
    placement: 'top',
    modifiers: [
      {
        name: 'offset',
        options: { offset: [0, 8] },
      },
      {
        name: 'arrow',
        options: { element: arrowElement },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom'],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          boundary: 'viewport',
          padding: 5,
        },
      },
      {
        name: 'applyArrowStyles',
        phase: 'write',
        enabled: true,
        fn({ state }) {
          const placement = state.placement
          if (placement.startsWith('bottom')) {
            arrowElement.style.top = '-4px'
          } else {
            arrowElement.style.top = ''
          }
        },
      },
    ],
  })

  const showTooltip = () => {
    tooltipElement.style.display = 'block'
    popperInstance.update()
  }

  const hideTooltip = () => {
    tooltipElement.style.display = 'none'
  }

  const checkInterval = setInterval(() => {
    if (!document.body.contains(targetElement)) removeTooltip()
  }, 300)

  const removeTooltip = () => {
    if (options.allowHoveringContent) {
      targetElement.removeEventListener('mouseenter', showTooltip)
      targetElement.removeEventListener('mouseleave', hideTooltip)
    }
    popperInstance.destroy()
    tooltipElement.remove()
    clearInterval(checkInterval)
  }

  hideTooltip()
  if (options.showForMs) {
    showTooltip()
    setTimeout(removeTooltip, options.showForMs)
  }

  if (options.allowHoveringContent) {
    targetElement.addEventListener('mouseenter', showTooltip)
    targetElement.addEventListener('mouseleave', hideTooltip)
  }

  const tooltip: ImperitiveTooltip = {
    showTooltip,
    hideTooltip,
    removeTooltip,
  }

  targetElement.imperitiveTooltip = tooltip

  return tooltip
}

/**
 * Converts the `tooltip` attribute of child elements into tooltips.
 * See `showImperitiveTooltip` for more details on the limitations of this tooltip.
 */
export const convertChildrensAttrToTooltip = (node: Element) => {
  const nodes = node.querySelectorAll('[tooltip]')
  nodes.forEach((n) => {
    const tooltip = n.getAttribute('tooltip')
    if (tooltip) showImperitiveTooltip(n, tooltip, { allowHoveringContent: true })
  })
}
